import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  XMarkIcon,
  PhoneIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import { useCustomers } from '../../hooks/useCustomers';
import { Deal, DealFormData, DealType } from '../../types/deal';
import { useLanguage } from '../../contexts/LanguageContext';

// Kereshető dropdown komponens
interface SearchableDropdownProps {
  options: Array<{ id: string; name: string }>;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  error?: string;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  error
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // A kiválasztott érték neve
  const selectedOption = options.find(option => option.id === value);

  // Szűrt opciók
  const filteredOptions = searchTerm.trim() === ''
    ? options
    : options.filter(option =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

  // Külső kattintás figyelése
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Ha van kiválasztott elem, annak nevét mutatjuk a keresőben
  useEffect(() => {
    if (selectedOption && !isOpen) {
      setSearchTerm(selectedOption.name);
    }
  }, [selectedOption, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTerm = e.target.value;
    setSearchTerm(newTerm);

    // Ha törlik a keresési kifejezést, és van kiválasztott érték, töröljük azt is
    if (newTerm === '' && value !== '') {
      onChange('');
    }

    // Keresés indításakor nyissuk meg a dropdownt
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Keresőmező */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          className={`block w-full pl-10 pr-10 py-2 border ${error ? 'border-red-500' : 'border-gray-300'} rounded-md leading-5 bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm shadow-sm`}
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
        >
          <ChevronDownIcon className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Dropdown lista */}
      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          <div className="py-1">
            {filteredOptions.map(option => (
              <div
                key={option.id}
                className={`px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer ${option.id === value ? 'bg-primary-50 text-primary-700' : 'text-gray-700'}`}
                onClick={() => {
                  onChange(option.id);
                  setSearchTerm(option.name);
                  setIsOpen(false);
                }}
              >
                {option.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

interface DealFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: DealFormData) => Promise<void>;
  initialData?: Deal;
  customerId?: string;
}

const DealForm: React.FC<DealFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  customerId
}) => {
  const { customers, loading: customersLoading } = useCustomers();
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, control, reset, setValue, watch, formState: { errors } } = useForm<DealFormData>({
    defaultValues: {
      type: initialData?.type || 'phone',
      customerId: initialData?.customerId || customerId || '',
      date: initialData?.date ? new Date(initialData.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      description: initialData?.description || '',
      outcome: initialData?.outcome || '',
      followUpDate: initialData?.followUpDate ? new Date(initialData.followUpDate).toISOString().split('T')[0] : undefined,
      subject: initialData?.subject || '',
      status: initialData?.status || 'pending'
    }
  });

  const dealType = watch('type');
  const selectedCustomerId = watch('customerId');

  useEffect(() => {
    if (isOpen) {
      reset({
        type: initialData?.type || 'phone',
        customerId: initialData?.customerId || customerId || '',
        date: initialData?.date ? new Date(initialData.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        description: initialData?.description || '',
        outcome: initialData?.outcome || '',
        followUpDate: initialData?.followUpDate ? new Date(initialData.followUpDate).toISOString().split('T')[0] : undefined,
        subject: initialData?.subject || '',
        status: initialData?.status || 'pending'
      });
    }
  }, [isOpen, initialData, customerId, reset]);

  const submitHandler = async (data: DealFormData) => {
    try {
      setIsSubmitting(true);
      await onSubmit(data);
      onClose();
    } catch (error) {
      console.error('Error submitting deal form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCustomerName = (customer: any) => {
    if (customer.type === 'organization') {
      return customer.companyName;
    }
    return `${customer.firstName} ${customer.lastName}`;
  };

  // Formázott ügyfelek a dropdown számára
  const customerOptions = customers?.map(customer => ({
    id: customer.id,
    name: formatCustomerName(customer)
  })) || [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? t('deals.edit') : t('deals.new')}
      size="md"
    >
      <form onSubmit={handleSubmit(submitHandler)} className="space-y-4 p-4">
        {/* Deal Type */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {t('deals.fields.type')}
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                value="phone"
                {...register('type')}
                className="h-4 w-4 text-primary-600"
              />
              <PhoneIcon className="h-5 w-5 text-blue-500" />
              <span className="text-gray-700">{t('deals.types.phone')}</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                value="email"
                {...register('type')}
                className="h-4 w-4 text-primary-600"
              />
              <EnvelopeIcon className="h-5 w-5 text-indigo-500" />
              <span className="text-gray-700">{t('deals.types.email')}</span>
            </label>
          </div>
        </div>

        {/* Customer Selection - Only show if customerId is not provided */}
        {!customerId && (
          <div className="space-y-2">
            <label htmlFor="customerId" className="block text-sm font-medium text-gray-700">
              {t('deals.fields.customer')}
            </label>
            <Controller
              name="customerId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SearchableDropdown
                  options={customerOptions}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t('common.placeholders.selectCustomer')}
                  error={errors.customerId ? "This field is required" : undefined}
                />
              )}
            />
          </div>
        )}

        {/* Date */}
        <div className="space-y-2">
          <label htmlFor="date" className="block text-sm font-medium text-gray-700">
            {t('deals.fields.date')}
          </label>
          <input
            type="date"
            id="date"
            {...register('date', { required: true })}
            className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 shadow-sm"
          />
          {errors.date && <p className="text-red-500 text-xs">This field is required</p>}
        </div>

        {/* Subject - Only for email */}
        {dealType === 'email' && (
          <div className="space-y-2">
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
              {t('deals.fields.subject')}
            </label>
            <input
              type="text"
              id="subject"
              {...register('subject', { required: dealType === 'email' })}
              className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 shadow-sm"
              placeholder={t('deals.fields.subject')}
            />
            {errors.subject && <p className="text-red-500 text-xs">This field is required</p>}
          </div>
        )}

        {/* Description */}
        <div className="space-y-2">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            {t('deals.fields.description')}
          </label>
          <textarea
            id="description"
            rows={4}
            {...register('description', { required: true })}
            className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 shadow-sm"
            placeholder={t('deals.fields.description')}
          />
          {errors.description && <p className="text-red-500 text-xs">This field is required</p>}
        </div>

        {/* Outcome */}
        <div className="space-y-2">
          <label htmlFor="outcome" className="block text-sm font-medium text-gray-700">
            {t('deals.fields.outcome')}
          </label>
          <textarea
            id="outcome"
            rows={2}
            {...register('outcome')}
            className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 shadow-sm"
            placeholder={t('deals.fields.outcome')}
          />
        </div>

        {/* Follow Up Date */}
        <div className="space-y-2">
          <label htmlFor="followUpDate" className="block text-sm font-medium text-gray-700">
            {t('deals.fields.followUpDate')}
          </label>
          <input
            type="date"
            id="followUpDate"
            {...register('followUpDate')}
            className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 shadow-sm"
          />
        </div>

        {/* Status */}
        <div className="space-y-2">
          <label htmlFor="status" className="block text-sm font-medium text-gray-700">
            {t('deals.fields.status')}
          </label>
          <select
            id="status"
            {...register('status')}
            className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 shadow-sm"
          >
            <option value="pending">{t('deals.status.pending')}</option>
            <option value="completed">{t('deals.status.completed')}</option>
            <option value="rejected">{t('deals.status.rejected')}</option>
          </select>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md focus:outline-none"
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md focus:outline-none disabled:bg-primary-300 disabled:cursor-not-allowed"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default DealForm; 